/**
 * Footer menu of the game shell: the main bar from the CMS layout of the event (slots
 * first…fourth), the overflow behind the power button and the checkin actions. Pure module:
 * labels, custom page titles and the build features of the target come in as parameters.
 */

export const LAYOUT_SLOTS = ['first', 'second', 'third', 'fourth'];
export const DEFAULT_LAYOUT_LBG = ['map', 'info', 'highscore', 'tasks'];
export const DEFAULT_LAYOUT_QUIZ = ['tasks', 'highscore'];

const SUPPORTED_VALUES = ['map', 'tasks', 'info', 'highscore', 'custom1', 'custom2', 'custom3'];
const CUSTOM_PAGES = [1, 2, 3];

const ICONS = {
    map: 'mdi-map',
    tasks: 'mdi-star',
    info: 'mdi-information',
    highscore: 'mdi-format-list-numbered',
    custom: 'mdi-web',
    qr: 'mdi-qrcode',
    password: 'mdi-lock',
};

/** Slot values of a layout (JSON string or object) in slot order; unusable input gives empty slots. */
export function layoutSlots(layout) {
    let parsed = layout;
    if (typeof layout === 'string') {
        try {
            parsed = JSON.parse(layout);
        } catch (ex) {
            parsed = null;
        }
    }
    if (!parsed || typeof parsed !== 'object') {
        return LAYOUT_SLOTS.map(() => '');
    }
    return LAYOUT_SLOTS.map((slot) => (typeof parsed[slot] === 'string' ? parsed[slot].trim() : ''));
}

export function hasMainEntry(menu, key) {
    return !!menu && Array.isArray(menu.main) && menu.main.some((cell) => !!cell && cell.key === key);
}

/**
 * Builds { main, overflow, actions } for an event.
 * main: 2 or 4 cells in slot order, an empty or unsupported slot is null. 4 cells as soon as
 * slot 3 or 4 holds a supported value, the defaults when no slot does. map links to the mobile
 * map page or, on larger screens, to the task page with the map.
 * overflow: custom pages with a title that are not in main (1, 2, 3), then the build features.
 * actions: qr and password, each when the event has a spot with that checkin, titled from labels.
 */
export function buildFooterMenu(options = {}) {
    const { isQuiz = false, features = [], hasQrCheckin = false, hasPwCheckin = false } = options;
    const supported = layoutSlots(options.layout).map((value) => (SUPPORTED_VALUES.includes(value) ? value : ''));

    let cellValues;
    if (supported.some(Boolean)) {
        cellValues = supported[2] || supported[3] ? supported : supported.slice(0, 2);
    } else {
        cellValues = isQuiz ? DEFAULT_LAYOUT_QUIZ : DEFAULT_LAYOUT_LBG;
    }
    const main = cellValues.map((value) => (value ? entryFor(value, options) : null));
    const inMain = new Set(cellValues.filter(Boolean));

    const overflow = [];
    CUSTOM_PAGES.forEach((page) => {
        const key = `custom${page}`;
        if (!inMain.has(key) && customPageTitle(options.customPageTitles, page)) {
            overflow.push(entryFor(key, options));
        }
    });
    features.forEach((feature) => {
        overflow.push({
            key: feature.key,
            title: feature.title || '',
            link: `/event/${options.eventId}/${feature.key}`,
            icon: feature.icon,
        });
    });

    const labels = options.labels || {};
    const actions = [];
    if (hasQrCheckin) {
        actions.push({ key: 'qr', title: labels.qr || '', icon: ICONS.qr });
    }
    if (hasPwCheckin) {
        actions.push({ key: 'password', title: labels.password || '', icon: ICONS.password });
    }

    return { main, overflow, actions };
}

function customPageTitle(titles, page) {
    return titles && typeof titles[page] === 'string' ? titles[page].trim() : '';
}

function entryFor(value, { eventId, isMobile = false, labels = {}, customPageTitles }) {
    const base = `/event/${eventId}`;
    const label = (key) => labels[key] || '';
    switch (value) {
        case 'map':
            return { key: 'map', title: label('map'), link: isMobile ? `${base}/map` : `${base}/tasks/map`, icon: ICONS.map };
        case 'tasks':
            return { key: 'tasks', title: label('tasks'), link: `${base}/tasks/nomap`, icon: ICONS.tasks };
        case 'info':
            return { key: 'info', title: label('info'), link: `${base}/info`, icon: ICONS.info };
        case 'highscore':
            return { key: 'highscore', title: label('highscore'), link: `${base}/highscore`, icon: ICONS.highscore };
        default: {
            const page = Number(value.replace('custom', ''));
            return { key: value, title: customPageTitle(customPageTitles, page), link: `${base}/custompage/${page}`, icon: ICONS.custom };
        }
    }
}
